import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useChitChat } from "../context/ChitChatContext";
import { useConversations } from "../context/ConversationsContext";

export const OpenChatHandler: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { conversations, getOrCreateConversation, userProfiles, loading } =
    useConversations();
  const { openChatWindow } = useChitChat();
  const lastProcessedUserIdRef = useRef<string | null>(null);
  const [pendingOpenChat, setPendingOpenChat] = useState<string | null>(null);

  const getUserName = (userId: string): string => {
    const profile = userProfiles[userId];
    return profile?.name || "Chat";
  };

  useEffect(() => {
    const openChatUserId = searchParams.get("openChat");
    const chatTs = searchParams.get("chatTs");
    const chatName = searchParams.get("chatName");
    const processKey = chatTs ? `${openChatUserId}-${chatTs}` : openChatUserId;

    if (openChatUserId && loading) {
      setPendingOpenChat(openChatUserId);
      return;
    }

    if (openChatUserId && processKey !== lastProcessedUserIdRef.current) {
      lastProcessedUserIdRef.current = processKey;
      setPendingOpenChat(null);

      const openChatWithUser = async () => {
        try {
          const existingConversation = conversations.find(
            (conv) =>
              conv.type === "direct" &&
              conv.participants?.includes(openChatUserId),
          );

          const userName = chatName || getUserName(openChatUserId);

          if (existingConversation) {
            const finalTitle =
              chatName ||
              (existingConversation.title !== "Chat"
                ? existingConversation.title
                : null) ||
              userName;
            openChatWindow({
              conversationId: existingConversation.id,
              conversationType: "direct",
              title: finalTitle || userName,
              user: { id: openChatUserId } as any,
            });
          } else {
            const conversationId =
              await getOrCreateConversation(openChatUserId);
            if (conversationId) {
              openChatWindow({
                conversationId,
                conversationType: "direct",
                title: userName,
                user: { id: openChatUserId } as any,
              });
            }
          }

          searchParams.delete("openChat");
          searchParams.delete("chatTs");
          searchParams.delete("chatName");
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          console.error(
            "[OpenChatHandler] Error opening chat from URL:",
            error,
          );
        }
      };

      openChatWithUser();
    }
  }, [
    searchParams,
    conversations,
    getOrCreateConversation,
    setSearchParams,
    openChatWindow,
    loading,
  ]);

  useEffect(() => {
    if (pendingOpenChat && !loading) {
      const chatName = searchParams.get("chatName");
      const userName = chatName || getUserName(pendingOpenChat);

      const processPendingChat = async () => {
        try {
          const existingConversation = conversations.find(
            (conv) =>
              conv.type === "direct" &&
              conv.participants?.includes(pendingOpenChat),
          );

          if (existingConversation) {
            const finalTitle =
              chatName ||
              (existingConversation.title !== "Chat"
                ? existingConversation.title
                : null) ||
              userName;
            openChatWindow({
              conversationId: existingConversation.id,
              conversationType: "direct",
              title: finalTitle || userName,
              user: { id: pendingOpenChat } as any,
            });
          } else {
            const conversationId =
              await getOrCreateConversation(pendingOpenChat);
            if (conversationId) {
              openChatWindow({
                conversationId,
                conversationType: "direct",
                title: userName,
                user: { id: pendingOpenChat } as any,
              });
            }
          }

          searchParams.delete("openChat");
          searchParams.delete("chatTs");
          searchParams.delete("chatName");
          setSearchParams(searchParams, { replace: true });
          lastProcessedUserIdRef.current = pendingOpenChat;
          setPendingOpenChat(null);
        } catch (error) {
          console.error(
            "[OpenChatHandler] Error processing pending chat:",
            error,
          );
          setPendingOpenChat(null);
        }
      };

      processPendingChat();
    }
  }, [
    pendingOpenChat,
    loading,
    conversations,
    getOrCreateConversation,
    openChatWindow,
    searchParams,
    setSearchParams,
    userProfiles,
  ]);

  return null;
};

export default OpenChatHandler;
